// src/modules/competitions/services/videoTypesService.ts

import { httpClient } from '../../../shared/services/httpClient';
import { API_ENDPOINTS } from '../../../shared/services/apiConfig';
import {
  VideoTypesApiResponse,
  VideoCriteriaApiResponse,
  ExampleVideosApiResponse,
  DeleteVideoSubmissionApiResponse,
  VideoUploadApiResponse,
  CompetitionVideoType,
} from '../types';

// 🆕 Interfaz para respuesta de token temporal
interface VideoTokenResponse {
  token: string;
  expires_in: number;
  participant_id: number;
  video_type_id: number;
  type: 'participant' | 'example';
}

class VideoTypesService {
  // Cache de tokens temporales
  private tokenCache: Map<string, { token: string; expiresAt: number }> = new Map();

  /**
   * Obtener tipos de video por categoría y nivel
   */
  async getVideoTypesByCategory(
    categoryId: number,
    level: string
  ): Promise<VideoTypesApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.COMPETITIONS.GET_VIDEO_TYPES, {
      category_id: categoryId,
      level: level,
    });
  }

  /**
   * Obtener criterios de evaluación por tipo de video
   */
  async getCriteriaByVideoType(
    videoTypeId: number
  ): Promise<VideoCriteriaApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.COMPETITIONS.GET_VIDEO_CRITERIA, {
      video_type_id: videoTypeId,
    });
  }

  /**
   * Obtener videos ejemplo por tipo de video
   */
  async getExampleVideos(videoTypeId: number): Promise<ExampleVideosApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.COMPETITIONS.GET_EXAMPLE_VIDEOS, {
      video_type_id: videoTypeId,
    });
  }

  /**
   * Subir video de participante
   */
  async submitVideo(formData: FormData): Promise<VideoUploadApiResponse> {
    return httpClient.postFormData<any>(
      API_ENDPOINTS.COMPETITIONS.SUBMIT_VIDEO,
      formData
    );
  }

  /**
   * Eliminar video subido
   */
  async deleteVideo(
    participantId: number,
    videoTypeId: number
  ): Promise<DeleteVideoSubmissionApiResponse> {
    return httpClient.delete<null>(API_ENDPOINTS.COMPETITIONS.DELETE_VIDEO, {
      participant_id: participantId,
      video_type_id: videoTypeId,
    });
  }

  /**
   * 🆕 Generar token temporal para video de participante
   */
  async generateVideoToken(
    participantId: number,
    videoTypeId: number,
    type: 'participant' | 'example' = 'participant'
  ): Promise<string | null> {
    try {
      // Crear clave de cache
      const cacheKey = `${type}_${participantId}_${videoTypeId}`;
      
      // Verificar si hay token en cache y no ha expirado
      const cached = this.tokenCache.get(cacheKey);
      if (cached && cached.expiresAt > Date.now()) {
        return cached.token;
      }

      // Solicitar nuevo token
      const response = await httpClient.post<VideoTokenResponse>(
        API_ENDPOINTS.COMPETITIONS.GENERATE_VIDEO_TOKEN,
        {
          participant_id: participantId,
          video_type_id: videoTypeId,
          type: type,
        }
      );

      if (response.success && response.data) {
        const token = response.data.token;
        const expiresIn = response.data.expires_in || 900; // 15 minutos por defecto
        
        // Guardar en cache (con 30 segundos de buffer antes de expiración)
        this.tokenCache.set(cacheKey, {
          token: token,
          expiresAt: Date.now() + (expiresIn - 30) * 1000,
        });

        return token;
      }

      return null;
    } catch (error) {
      console.error('Error generando token de video:', error);
      return null;
    }
  }

  /**
   * 🆕 Construir URL con token temporal para video de participante
   */
  async getParticipantVideoUrlWithToken(
    participantId: number,
    videoTypeId: number
  ): Promise<string | null> {
    const token = await this.generateVideoToken(participantId, videoTypeId, 'participant');
    
    if (!token) {
      console.error('No se pudo generar token para video');
      return null;
    }

    const baseUrl = API_ENDPOINTS.COMPETITIONS.GET_PARTICIPANT_VIDEO;
    return `/api/api${baseUrl}?participant_id=${participantId}&video_type_id=${videoTypeId}&token=${token}`;
  }

  /**
   * 🆕 Construir URL con token temporal para video ejemplo
   */
  async getExampleVideoUrlWithToken(
    exampleId: number,
    participantId: number,
    videoTypeId: number
  ): Promise<string | null> {
    const token = await this.generateVideoToken(participantId, videoTypeId, 'example');
    
    if (!token) {
      console.error('No se pudo generar token para video ejemplo');
      return null;
    }

    const baseUrl = API_ENDPOINTS.COMPETITIONS.GET_EXAMPLE_VIDEO;
    return `/api/api${baseUrl}?id=${exampleId}&token=${token}`;
  }

  /**
   * ⚠️ DEPRECATED: Usar getParticipantVideoUrlWithToken() en su lugar
   * Construir URL para obtener video del participante (SIN token temporal)
   */
  getParticipantVideoUrl(participantId: number, videoTypeId: number): string {
    console.warn('getParticipantVideoUrl() está deprecado. Usar getParticipantVideoUrlWithToken()');
    const baseUrl = API_ENDPOINTS.COMPETITIONS.GET_PARTICIPANT_VIDEO;
    return `/api/api${baseUrl}?participant_id=${participantId}&video_type_id=${videoTypeId}`;
  }

  /**
   * ⚠️ DEPRECATED: Usar getExampleVideoUrlWithToken() en su lugar
   * Construir URL para obtener video ejemplo (SIN token temporal)
   */
  getExampleVideoUrl(exampleId: number): string {
    console.warn('getExampleVideoUrl() está deprecado. Usar getExampleVideoUrlWithToken()');
    const baseUrl = API_ENDPOINTS.COMPETITIONS.GET_EXAMPLE_VIDEO;
    return `/api/api${baseUrl}?id=${exampleId}`;
  }

  /**
   * Validar si un archivo de video cumple con los requisitos
   */
  validateVideoFile(
    file: File,
    videoType: CompetitionVideoType
  ): { isValid: boolean; error?: string } {
    const maxSizeBytes = videoType.max_file_size_mb * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return {
        isValid: false,
        error: `El archivo es muy grande. Tamaño máximo: ${videoType.max_file_size_mb}MB`,
      };
    }

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !videoType.allowed_formats.includes(fileExtension)) {
      return {
        isValid: false,
        error: `Formato no permitido. Formatos aceptados: ${videoType.allowed_formats.join(', ')}`,
      };
    }

    const validMimeTypes: { [key: string]: string[] } = {
      'mp4': ['video/mp4'],
      'mov': ['video/quicktime'],
      'avi': ['video/x-msvideo', 'video/avi'],
      'webm': ['video/webm'],
    };

    const allowedMimeTypes: string[] = [];
    videoType.allowed_formats.forEach(format => {
      if (validMimeTypes[format]) {
        allowedMimeTypes.push(...validMimeTypes[format]);
      }
    });

    if (!allowedMimeTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'Tipo de archivo no válido',
      };
    }

    return { isValid: true };
  }

  /**
   * Validar duración de video
   */
  async validateVideoDuration(
    file: File,
    maxDurationSeconds: number
  ): Promise<{ isValid: boolean; duration?: number; error?: string }> {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const duration = Math.floor(video.duration);

        if (duration > maxDurationSeconds) {
          resolve({
            isValid: false,
            duration,
            error: `El video es muy largo. Duración máxima: ${maxDurationSeconds} segundos`,
          });
        } else {
          resolve({
            isValid: true,
            duration,
          });
        }
      };

      video.onerror = () => {
        resolve({
          isValid: false,
          error: 'No se pudo leer la duración del video',
        });
      };

      video.src = URL.createObjectURL(file);
    });
  }

  /**
   * Formatear tamaño de archivo
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Formatear duración en formato mm:ss
   */
  formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * 🆕 Limpiar cache de tokens
   */
  clearTokenCache(): void {
    this.tokenCache.clear();
  }
}

export const videoTypesService = new VideoTypesService();